import React, { useState, useEffect } from "react";
import instance from "../../instance";

import "../Customer.css";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PaymentDetails = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [paymentData, setPaymentData] = useState(null);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    PriceType: "",
    Price: "",
    PaymentDate: null,
    SystemPrice: "",
    Prepaid: "",
    MonthlyFees: "",
    Notes: "",
  });

  useEffect(() => {
    if (code) {
      fetchPaymentData();
    } else {
      toast.error("No code provided.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPaymentData is recreated each render; adding it would refetch on every paint
  }, [code]);

  const fetchPaymentData = async () => {
    try {
      const response = await instance.get(`payment/by-code/${code}`);

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = response.data;
      if (data && data.data) {
        setPaymentData(data.data);
        setPaymentFormData({
          PriceType: data.data.PriceType || "",
          Price: data.data.Price || "",
          PaymentDate: data.data.PaymentDate
            ? new Date(data.data.PaymentDate)
            : null,
          SystemPrice: data.data.SystemPrice || "",
          Prepaid: data.data.Prepaid || "",
          MonthlyFees: data.data.MonthlyFees || "",
          Notes: data.data.Notes || "",
        });
        calculateRemainingAmount(data.data.SystemPrice, data.data.Prepaid);
        calculateDaysRemaining(data.data.PaymentDate);
      } else {
        toast.error("No payment data found for the given code.");
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("An error occurred while fetching payment data.");
    }
  };

  const calculateDaysRemaining = (paymentDate) => {
    if (paymentDate) {
      const today = new Date();
      const diffTime = new Date(paymentDate) - today;
      const daysDiff = Math.ceil(diffTime / (1000 * 3600 * 24));
      setDaysRemaining(Math.abs(daysDiff));
    } else {
      setDaysRemaining(null);
    }
  };

  const toggleEditPayment = () => {
    if (isEditingPayment) {
      savePaymentData();
    } else {
      setIsEditingPayment(true);
    }
  };

  const handlePaymentInputChange = (event) => {
    const { name, value } = event.target;
    setPaymentFormData({ ...paymentFormData, [name]: value });
  };

  const handleDateChange = (date) => {
    setPaymentFormData({ ...paymentFormData, PaymentDate: date });
    calculateDaysRemaining(date);
  };

  const savePaymentData = async () => {
    const formattedData = {
      ...paymentFormData,
      PaymentDate: paymentFormData.PaymentDate
        ? paymentFormData.PaymentDate.toISOString().split("T")[0]
        : null,
    };

    try {
      const response = await instance.put(
        `payment/by-code/${code}`,
        formattedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success("Payment details updated successfully!");
      fetchPaymentData(); // Fetch updated data instead of reloading the page
      setIsEditingPayment(false); // Exit editing mode
    } catch (error) {
      console.error("Error updating payment details:", error);
      toast.error("An error occurred while updating payment details.");
    }
  };

  const calculateRemainingAmount = (
    systemPrice = paymentFormData.SystemPrice,
    prepaid = paymentFormData.Prepaid
  ) => {
    const systemPriceFloat = parseFloat(systemPrice) || 0;
    const prepaidFloat = parseFloat(prepaid) || 0;
    setRemainingAmount(systemPriceFloat - prepaidFloat);
  };

  const isPaymentDateOverdue = (paymentDate) => {
    return new Date(paymentDate) < new Date();
  };

  return (
    <div>
      <main>
        <section id="payment-section">
          <h2>Payment Details</h2>
          {paymentData && (
            <ul>
              <li>
                Price Type:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-PriceType"
                  style={{
                    background:
                      paymentData.PriceType === "Bronze"
                        ? "rgba(165, 42, 42, 0.3)"
                        : paymentData.PriceType === "Silver"
                        ? "rgba(192, 192, 192, 0.3)"
                        : paymentData.PriceType === "Golden"
                        ? "rgba(255, 217, 0, 0.3)"
                        : paymentData.PriceType === "Platinum"
                        ? "rgba(83, 195, 232, 0.3)"
                        : "white",
                  }}
                >
                  {paymentData.PriceType}
                </span>
                {isEditingPayment && (
                  <select
                    className="input-field"
                    name="PriceType"
                    placeholder="Enter price type"
                    value={paymentFormData.PriceType}
                    onChange={handlePaymentInputChange}
                  >
                    <option value="Bronze">Bronze</option>
                    <option value="Silver">Silver</option>
                    <option value="Golden">Golden</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Diamond">Diamond</option>
                  </select>
                )}
              </li>
              <li>
                Price:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-Price"
                >
                  {paymentData.Price}
                </span>
                {isEditingPayment && (
                  <input
                    className="input-field"
                    type="text"
                    name="Price"
                    value={paymentFormData.Price}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter Price"
                  />
                )}
              </li>
              <li>
                Payment Date:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-PaymentDate"
                  style={{
                    fontSize: "16px",
                    backgroundColor:
                      paymentData.PaymentDate &&
                      new Date(paymentData.PaymentDate) <= new Date()
                        ? "rgba(255, 0, 0, 0.2)"
                        : "transparent",
                  }}
                >
                  {paymentData.PaymentDate &&
                    new Date(paymentData.PaymentDate).toLocaleDateString(
                      "en-GB"
                    )}
                </span>
                {isEditingPayment && (
                  <DatePicker
                    className="input-field"
                    selected={paymentFormData.PaymentDate}
                    onChange={handleDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Payment Date"
                  />
                )}
              </li>
              <li>
                Remaining Days:{" "}
                <span
                  className="info3"
                  id="remaining-days"
                  style={{
                    fontSize: "16px",
                    backgroundColor: isPaymentDateOverdue(
                      paymentData.PaymentDate
                    )
                      ? "rgba(255, 0, 0, 0.2)"
                      : "white",
                  }}
                >
                  {daysRemaining !== null ? daysRemaining : "0"}
                </span>
              </li>
              <li>
                System Price:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-SystemPrice"
                >
                  {paymentData.SystemPrice}
                </span>
                {isEditingPayment && (
                  <input
                    className="input-field"
                    type="text"
                    name="SystemPrice"
                    value={paymentFormData.SystemPrice}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter System Price"
                  />
                )}
              </li>
              <li>
                Prepaid:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-Prepaid"
                >
                  {paymentData.Prepaid}
                </span>
                {isEditingPayment && (
                  <input
                    className="input-field"
                    type="text"
                    name="Prepaid"
                    value={paymentFormData.Prepaid}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter Prepaid"
                  />
                )}
              </li>
              <li>
                Remaining Amount:{" "}
                <span
                  className="info"
                  id="remaining-amount"
                  style={{
                    fontSize: "20px",
                  }}
                >
                  {remainingAmount}
                </span>
              </li>
              <li>
                Monthly Fees:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-MonthlyFees"
                >
                  {paymentData.MonthlyFees}
                </span>
                {isEditingPayment && (
                  <input
                    className="input-field"
                    type="text"
                    name="MonthlyFees"
                    value={paymentFormData.MonthlyFees}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter Monthly Fees"
                  />
                )}
              </li>
              <li>
                Notes:{" "}
                <span
                  className={`info ${isEditingPayment ? "hidden" : ""}`}
                  id="info-Notes"
                >
                  {paymentData.Notes}
                </span>
                {isEditingPayment && (
                  <input
                    className="input-field"
                    type="text"
                    name="Notes"
                    value={paymentFormData.Notes}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter Notes"
                  />
                )}
              </li>
            </ul>
          )}
          <button className="edit-button" onClick={toggleEditPayment}>
            {isEditingPayment ? "Save" : "Update"}
          </button>
        </section>
      </main>
    </div>
  );
};

export default PaymentDetails;
